<template>
  <v-container class="will-verification-container">
    <div v-if="!loading">
      <v-row no-gutters>
        <v-col cols="12" sm="1">
          <router-link :to="{ name: 'OverviewPage' }" class="back-arrow"
            ><v-icon large color="mid-grey" class="mt-1">{{
              icons.mdiArrowLeft
            }}</v-icon></router-link
          >
        </v-col>
        <v-col cols="12" sm="11">
          <h1 class="mb-2">Will verification</h1>
        </v-col>
      </v-row>

      <v-row no-gutters class="mt-sm-6">
        <v-col cols="12" md="4" offset-md="1" class="form-blurb">
          <div class="will-verification-info-box mb-4">
            <div>
              <h3>What do we check for?</h3>
              <ul>
                <li>ensure will is correctly signed for validity</li>
                <li>ensure witnesses are legally permitted to sign</li>
              </ul>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="7">
          <div class="left-section-container">
            <v-row>
              <v-col cols="12">
                <h2 class="mb-2">Latest uploaded will</h2>
                <div v-if="latestUpload" class="latest-upload-container">
                  <div class="status-container">
                    <v-icon :color="latestUploadStatusDetails.color">{{
                      latestUploadStatusDetails.icon
                    }}</v-icon>
                    <p class="status-text ml-2">
                      Status:
                      <span
                        class="status-value"
                        :style="{
                          color: latestUploadStatusDetails.color,
                        }"
                        >{{ latestUploadStatusDetails.status }}</span
                      >
                    </p>
                  </div>
                  <div class="latest-upload-card">
                    <div class="upload-details-card">
                      <p class="mb-1">
                        <strong>Version:</strong> {{ latestVersion.version }}
                        &nbsp;&nbsp;|&nbsp;&nbsp;
                        <strong>Uploaded:</strong>
                        {{ formattedUploadDate }}
                      </p>

                      <div v-if="latestUpload.comments" class="mb-3">
                        <h5 class="mt-2">Comments:</h5>
                        <p>{{ latestUpload.comments }}</p>
                      </div>
                      <div
                        class="action-button-container"
                        style="margin-bottom: 0 !important"
                      >
                        <GradientButton
                          @gradientButtonClicked="
                            navigateToUploadedWill(latestUpload.id)
                          "
                          :title="'View most recent uploaded will'"
                          :callout="'right'"
                          :fill="false"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <p v-else>
                  You have not uploaded a signed will for the latest will
                  version
                </p>
              </v-col>
              <v-col v-if="showPreExistingWill" cols="12" md="6">
                <h2>Latest pre existing will</h2>
                <v-btn
                  rounded
                  depressed
                  large
                  color="secondary"
                  class="ml-0"
                  @click="navigateToUploadedPreExistingWill()"
                >
                  View your pre existing will
                </v-btn>
              </v-col>
            </v-row>

            <div v-if="!latestUploadIsValid">
              <v-divider class="my-6" />

              <h3>{{ uploadWillHeading }}</h3>
              <p>Only PDF documents allowed</p>
              <v-file-input
                v-model="file"
                single-line
                outlined
                dense
                filled
                :loading="buttonLoading"
                accept="application/pdf"
                label="Upload will"
                class="file-upload-btn"
              />
              <WillUploadProgress :loading="buttonLoading" />
            </div>

            <div
              v-if="!latestUploadIsValid && previousValidUpload"
              class="previous-valid-section"
            >
              <v-divider class="my-6" />

              <h2 class="mb-2">
                {{ previousValidUploadLabel }} will to be replaced
              </h2>
              <div class="latest-upload-container">
                <div class="status-container">
                  <v-icon color="#FF6F61">{{
                    icons.mdiAlertCircleOutline
                  }}</v-icon>
                  <p class="status-text ml-2">
                    Status:
                    <span class="status-value" style="color: #ed1623"
                      >To be replaced</span
                    >
                  </p>
                </div>
                <div class="latest-upload-card">
                  <div class="upload-details-card">
                    <p class="mb-1">
                      <strong>Version:</strong>
                      {{ previousWillVersion.version }}
                      &nbsp;&nbsp;|&nbsp;&nbsp;
                      <strong>Uploaded:</strong>
                      {{ formattedPreviousValidUploadDate }}
                    </p>

                    <div
                      v-if="
                        previousValidUpload.comments &&
                        previousValidUpload.status !== 'VALID'
                      "
                      class="mb-3"
                    >
                      <h5 class="mt-2">Comments:</h5>
                      <p>{{ previousValidUpload.comments }}</p>
                    </div>
                    <div
                      class="action-button-container"
                      style="margin-bottom: 0 !important"
                    >
                      <GradientButton
                        @gradientButtonClicked="
                          navigateToUploadedWill(previousValidUpload.id)
                        "
                        :title="'View uploaded will'"
                        :callout="'right'"
                        :fill="false"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <v-divider class="my-6" />
            </div>

            <div class="button-container">
              <GradientButton
                :disabled="buttonLoading == 'success'"
                :title="'Save And Continue'"
                :callout="'right'"
                :small="true"
                @gradientButtonClicked="navigateBack"
              />
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

    <div v-else class="text-center mt-12">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <ErrorSnackbar v-model="showError" :error="errorMessage" />

    <v-dialog v-model="identityMismatchDialog" max-width="560" persistent>
      <WillIdentityMismatchDialog
        :message="identityMismatchMessage"
        :prompt="identityMismatchPrompt"
        :loading="identityMismatchConfirming"
        @confirm="confirmIdentityMismatch"
        @cancel="cancelIdentityMismatch"
      />
    </v-dialog>

    <TestatorAssistanceGuide />
  </v-container>
</template>

<script>
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import {
    UPLOAD_WILL_MUTATION,
    CREATE_UPLOADED_WILL_PRESIGNED_URL_MUTATION,
    CREATE_PRE_EXISTING_WILL_PRESIGNED_URL_MUTATION,
  } from '../../constants/graphql/will-verifications';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import SuccessSnackbar from '../common/SuccessSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import { willIdentityMismatchMixin } from '../../mixins/will-identity-mismatch-mixin';
  import WillIdentityMismatchDialog from '../common/WillIdentityMismatchDialog.vue';
  import WillUploadProgress from '../common/WillUploadProgress.vue';
  import {
    verificationStatus,
    latestWillVersion,
    latestUploadedWill,
    latestValidUploadedWill,
  } from '../../constants/js/will-verification';
  import TestatorAssistanceGuide from '../common/dialogs/TestatorAssistanceGuide.vue';

  export default {
    name: 'WillVerification',
    components: {
      ErrorSnackbar,
      SuccessSnackbar,
      TestatorAssistanceGuide,
      WillIdentityMismatchDialog,
      WillUploadProgress,
    },
    mixins: [formHelperMixin, willIdentityMismatchMixin],
    data() {
      return {
        testator: {},
        file: null,
        loading: true,
        showSuccess: false,
        successMessage: null,
        errorMessage: null,
      };
    },
    computed: {
      uploadWillHeading() {
        return this.latestUpload ? 'Re-upload your will' : 'Upload your will';
      },
      latestUpload() {
        return latestUploadedWill(this.testator.willVersions);
      },
      latestValidUpload() {
        return latestValidUploadedWill(this.testator.willVersions);
      },
      latestUploadStatusDetails() {
        const details = verificationStatus(
          this.latestUpload.status,
          this.icons
        );
        const iconMap = {
          PENDING: this.icons.mdiInformationOutline,
          INVALID: this.icons.mdiAlertCircleOutline,
          VALIDITY_INCONCLUSIVE: this.icons.mdiAlertCircleOutline,
          VALID: this.icons.mdiCheckCircle,
        };
        const colorMap = {
          PENDING: '#87CEEB',
        };
        return {
          ...details,
          icon: iconMap[this.latestUpload.status] || details.icon,
          color: colorMap[this.latestUpload.status] || details.color,
        };
      },
      latestUploadIsValid() {
        return this.latestUpload && this.latestUpload.status == 'VALID';
      },
      showPreExistingWill() {
        return !this.latestUpload && this.latestPreExistingWill;
      },
      latestPreExistingWill() {
        if (!this.testator.preExistingWills.length) return null;

        return this.testator.preExistingWills.reduce((r, a) => {
          return new Date(r.createdAt) > new Date(a.createdAt) ? r : a;
        });
      },
      formattedUploadDate() {
        if (!this.latestUpload?.createdAt) return '';
        const date = new Date(this.latestUpload.createdAt);
        return date.toISOString().split('T')[0];
      },
      latestVersion() {
        return latestWillVersion(this.testator?.willVersions);
      },
      previousWillVersion() {
        const versions = this.testator.willVersions;
        if (!versions || versions.length < 2) return null;
        const sorted = [...versions].sort((a, b) => b.version - a.version);
        return sorted[1];
      },
      previousValidUpload() {
        if (!this.previousWillVersion) return null;
        return latestUploadedWill([this.previousWillVersion]);
      },
      previousValidUploadLabel() {
        const status = this.previousValidUpload?.status;
        if (status === 'VALID') return 'Valid';
        if (status === 'INVALID') return 'Invalid';
        if (status === 'VALIDITY_INCONCLUSIVE') return 'Inconclusive';
        return 'Current';
      },
      formattedPreviousValidUploadDate() {
        if (!this.previousValidUpload?.createdAt) return '';
        const date = new Date(this.previousValidUpload.createdAt);
        return date.toISOString().split('T')[0];
      },
    },
    watch: {
      file(newValue) {
        if (newValue) {
          this.submit();
        }
      },
    },
    methods: {
      submit() {
        this.buttonLoading = 'success';
        var reader = new FileReader();
        reader.readAsDataURL(this.file);
        reader.onload = function (evt) {
          const fileBase64 = evt.target.result;
          this.uploadWill(fileBase64);
        }.bind(this);
      },
      uploadWill(fileBase64) {
        this.performUpload(fileBase64, false);
      },
      performUpload(fileBase64, acknowledgedMismatch) {
        const testatorId = this.testatorId;

        this.$apollo
          .mutate({
            mutation: UPLOAD_WILL_MUTATION,
            variables: {
              testatorId,
              fileBase64,
              acknowledgedMismatch,
            },
            update: (cache, { data: { uploadWill } }) => {
              this.updateCacheAfterUploadWill(cache, uploadWill);
            },
          })
          .then(_response => {
            this.buttonLoading = false;
            this.file = null;
            this.showSuccessMessage();
            this.$store.dispatch('evaluateGuideRules', {
              testator: this.testator,
              steps: this.testator.steps,
              page: 'will-verification',
              willUploadSuccess: true,
            });
          })
          .catch(error => {
            if (this.handleIdentityMismatch(error, fileBase64)) return;

            if (error.message.includes('GraphQL error: Invalid file type')) {
              this.errorMessage = 'Invalid file type. Please upload a pdf.';
            } else {
              this.reportUnexpectedUploadError(error);
              return;
            }
            this.displayError();
            this.file = null;
          });
      },
      updateCacheAfterUploadWill(cache, uploadWill) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var willVersion = data.testator.willVersions.find(
          willVersion => willVersion.id == uploadWill.willVersionId
        );
        willVersion.uploadedWills.push(uploadWill);

        cache.writeQuery({ ...query, data });
      },
      showSuccessMessage() {
        this.successMessage = 'Will successfully uploaded';
        this.showSuccess = true;
      },
      navigateToUploadedWill(id) {
        var newTab = window.open('', '_blank');
        this.$apollo
          .mutate({
            mutation: CREATE_UPLOADED_WILL_PRESIGNED_URL_MUTATION,
            variables: {
              id,
            },
          })
          .then(response => {
            newTab.location.href = response.data.createUploadedWillPresignedUrl;
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      navigateToUploadedPreExistingWill() {
        var newTab = window.open('', '_blank');
        const id = this.latestPreExistingWill.id;
        this.$apollo
          .mutate({
            mutation: CREATE_PRE_EXISTING_WILL_PRESIGNED_URL_MUTATION,
            variables: {
              id,
            },
          })
          .then(response => {
            newTab.location.href =
              response.data.createPreExistingWillPresignedUrl;
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      navigateBack() {
        this.$router.back();
      },
    },
    apollo: {
      testator: {
        query: TESTATOR_QUERY,
        variables() {
          return {
            id: this.testatorId,
          };
        },
        result(response) {
          if (response.data) {
            if (response.data.testator.willVersions.length) {
              this.loading = false;
            } else {
              this.$router.push({ name: 'OverviewPage' });
            }
          }
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  .will-verification-container {
    padding-top: 40px;
    padding-bottom: 60px;

    .will-verification-info-box {
      // border in global.scss because of IE issue
      border-radius: 7px;
      padding: 15px;
    }
    .left-section-container {
      padding-left: 121px;
    }

    .latest-upload-card {
      margin: 0;
      padding: 0;
      overflow: hidden;

      .upload-details-card {
        border-top: 2px solid #dcddde;
        border-left: 4px solid #429378;
        padding: 30px 30px 40px 30px;
        background: #f2f2f2;
      }

      .upload-separator {
        border: none;
        border-top: 2px solid #dcddde;
        margin-top: 16px;
      }
    }

    .action-button-container {
      margin-top: 30px;
      margin-bottom: 40px;
    }

    .latest-upload-container {
      background-color: white;
      .status-container {
        display: flex;
        align-items: center;
        padding: 0 0 0 23px;
        height: 80px;
        box-sizing: border-box;

        .status-text {
          font-weight: 700;
          font-size: 16px;
          line-height: 150%;
          color: #282828;
          margin: 0;

          .status-value {
            font-weight: 600;
          }
        }
      }
    }
  }

  @media only screen and (min-width: 0px) and (max-width: 959px) {
    .will-verification-container {
      .left-section-container {
        padding-left: 0;
      }
    }
  }
</style>
